import { signal } from '@preact/signals'
import type { ReadonlySignal } from '@preact/signals'

/** How the OS lays out: one app at a time on the phone, windows on the desktop. */
export type OsForm = 'phone' | 'desktop'

/** A rectangle in CSS pixels, relative to the OS's screen. */
export interface OsRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/** A running app. On the desktop it's a window; `x` and `y` are its top left, in points. */
export interface OsWindow {
  readonly app: string
  readonly minimised: boolean
  readonly zoomed: boolean
  readonly x: number
  readonly y: number
  /** Stacking order: higher is in front. */
  readonly z: number
  /** The launcher icon it opened from, for the zoom; null when unknown. */
  readonly origin: OsRect | null
}

/**
 * One device's OS state: the running apps and the one in front. Presentation only: no game
 * data lives here, so a reload (a new level session) starts it clean.
 */
export interface OsState {
  readonly form: OsForm
  readonly windows: ReadonlySignal<readonly OsWindow[]>
  /** The app in front, or null on the home screen (phone) or the bare desktop. */
  readonly front: ReadonlySignal<string | null>
  /** Opens `app`, or brings it to the front (restored) when it's running. */
  open(app: string, origin?: OsRect | null): void
  close(app: string): void
  minimise(app: string): void
  /** Toggles filling the desktop. */
  zoom(app: string): void
  focus(app: string): void
  move(app: string, x: number, y: number): void
  /** The phone's home screen: nothing in front, the apps keep running. */
  home(): void
  /**
   * Lets `app` take the system Back while it's in front (its own back stack): `handler`
   * returns true when it went back. null removes it.
   */
  setBack(app: string, handler: (() => boolean) | null): void
  /** The front app's Back: true when it handled it, false when it has nowhere to go. */
  back(): boolean
}

/** Where the first window opens, and how far each next one steps, in points. */
const CASCADE = { x: 140, y: 56, step: 28, max: 6 } as const

export function createOsState(form: OsForm): OsState {
  const windows = signal<readonly OsWindow[]>([])
  const front = signal<string | null>(null)
  const backs = new Map<string, () => boolean>()
  let top = 0

  const update = (app: string, patch: Partial<OsWindow>): void => {
    windows.value = windows.value.map((w) => (w.app === app ? { ...w, ...patch } : w))
  }
  const raise = (app: string, patch: Partial<OsWindow> = {}): void => {
    top += 1
    update(app, { ...patch, z: top, minimised: false })
    front.value = app
  }
  // The phone goes home when its app leaves; the desktop brings the next window forward.
  const nextFront = (): string | null => {
    if (form === 'phone') return null
    const shown = windows.value.filter((w) => !w.minimised)
    return (
      shown.reduce<OsWindow | null>((a, b) => (a === null || b.z > a.z ? b : a), null)?.app ?? null
    )
  }

  return {
    form,
    windows,
    front,
    open(app, origin = null) {
      if (windows.value.some((w) => w.app === app)) {
        raise(app, origin ? { origin } : {})
        return
      }
      top += 1
      const step = (windows.value.length % CASCADE.max) * CASCADE.step
      const window: OsWindow = {
        app,
        minimised: false,
        zoomed: false,
        x: CASCADE.x + step,
        y: CASCADE.y + step,
        z: top,
        origin,
      }
      windows.value = [...windows.value, window]
      front.value = app
    },
    close(app) {
      windows.value = windows.value.filter((w) => w.app !== app)
      if (front.value === app) front.value = nextFront()
    },
    minimise(app) {
      update(app, { minimised: true })
      if (front.value === app) front.value = nextFront()
    },
    zoom(app) {
      const window = windows.value.find((w) => w.app === app)
      if (window) raise(app, { zoomed: !window.zoomed })
    },
    focus(app) {
      const window = windows.value.find((w) => w.app === app)
      if (window && (front.value !== app || window.minimised)) raise(app)
    },
    move(app, x, y) {
      update(app, { x, y })
    },
    home() {
      front.value = null
    },
    setBack(app, handler) {
      if (handler) backs.set(app, handler)
      else backs.delete(app)
    },
    back() {
      const app = front.value
      return app !== null && (backs.get(app)?.() ?? false)
    },
  }
}
